import { PropsWithChildren } from 'react'
import { useSelector } from 'react-redux'
import { PersonalizationWidgetConfiguration, PersonalizationWidgetProvider } from '@vp/design-personalization-widget'
import { useUserContextTracker } from '@vp/ubik-context'

import { PersonalizationWidgetProviderWrapperProps } from '~/app/features/FragmentRoot/contexts/personalization/interface'
import { usePersonalizationConfig } from '~/app/features/Gallery/hooks/usePersonalizationConfig'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { getGalleryIdSelector } from '~/app/store/config'
import { isPpagGallerySelector } from '~/app/store/config/reducer'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { LOGGER_SERVICE_NAME } from '~/shared/constants'
import { FEATURE_GATES } from '~/shared/featureGates/constants'

export const PersonalizationWidgetProviderWrapper = ({ localizations, children }: PropsWithChildren<PersonalizationWidgetProviderWrapperProps>) => {
  const { imageCopyKey, templateFields } = usePersonalizationConfig()
  const galleryId = useSelector(getGalleryIdSelector).toString()
  const isPpagGallery = useSelector(isPpagGallerySelector)
  const analytics = useAnalytics()

  const isPhoto = imageCopyKey === 'Photo'
  const isColorPersonalizationEnabled = useFeatureGate(FEATURE_GATES.GALLERY_COLOR_PERSONALIZATION)
  const shouldShowColorPersonalization = !isPpagGallery && isColorPersonalizationEnabled

  const tracker = useUserContextTracker()
  tracker({ type: 'featureFlag', name: FEATURE_GATES.GALLERY_COLOR_PERSONALIZATION, enabled: String(isColorPersonalizationEnabled) })

  const personalizationWidgetConfig: PersonalizationWidgetConfiguration = {
    textConfig: {
      enabledTextFields: templateFields,
    },
    imageConfig: {
      maxImages: 10,
      classifyImagesAsPhotos: isPhoto,
    },
    createBrandIfNoneExists: !isPhoto,
    allowRestoreFromBrand: !isPhoto,
    ...(shouldShowColorPersonalization && { colorConfig: { showColorPicker: true } }),
  }

  const trackingData = {
    ...analytics.getProductData(),
  }

  return (
    <PersonalizationWidgetProvider
      localizations={localizations}
      configuration={personalizationWidgetConfig}
      localStoragePageKey={galleryId}
      trackingData={trackingData}
      hostName={LOGGER_SERVICE_NAME}
    >
      {children}
    </PersonalizationWidgetProvider>
  )
}
